import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ChartOfAccount {
  id: string;
  code: string;
  name: string;
  name_pt: string | null;
  type: 'revenue' | 'cac' | 'fulfillment' | 'ga' | 'financial';
  category: string | null;
  parent_code: string | null;
  level: number;
  cost_behavior: 'fixed' | 'variable' | 'mixed' | null;
  is_active: boolean;
  display_order: number | null;
  created_at: string | null;
}

export function useChartOfAccounts() {
  return useQuery({
    queryKey: ['chart-of-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as ChartOfAccount[];
    },
  });
}

export function useActiveChartOfAccounts() {
  return useQuery({
    queryKey: ['chart-of-accounts', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as ChartOfAccount[];
    },
  });
}

export function useChartOfAccountsByType(type: ChartOfAccount['type']) {
  return useQuery({
    queryKey: ['chart-of-accounts', 'type', type],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('type', type)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as ChartOfAccount[];
    },
  });
}

export function useUpdateChartOfAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<ChartOfAccount> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('chart_of_accounts')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result as ChartOfAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}

export function useCreateChartOfAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<ChartOfAccount, 'id' | 'created_at'>) => {
      const { data: result, error } = await supabase
        .from('chart_of_accounts')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result as ChartOfAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}

// Helper to build hierarchical structure
export function buildAccountHierarchy(accounts: ChartOfAccount[]) {
  const rootAccounts = accounts.filter(a => !a.parent_code);
  
  const getChildren = (parentCode: string): ChartOfAccount[] => {
    return accounts
      .filter(a => a.parent_code === parentCode)
      .map(account => ({
        ...account,
        children: getChildren(account.code),
      }));
  };

  return rootAccounts.map(account => ({
    ...account,
    children: getChildren(account.code),
  }));
}

// Type labels for display
export const ACCOUNT_TYPE_LABELS: Record<ChartOfAccount['type'], string> = {
  revenue: 'Receita',
  cac: 'CAC - Aquisição',
  fulfillment: 'Entrega',
  ga: 'G&A - Administrativo',
  financial: 'Financeiro',
};

export const COST_BEHAVIOR_LABELS: Record<string, string> = {
  fixed: 'Fixo',
  variable: 'Variável',
  mixed: 'Misto',
};
